import { forwardRef } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import ArrowForwardSvg from 'icons/monochrome/arrow-ios-forward.svg';
import InfoSvg from 'icons/monochrome/info.svg';

import { navTestId } from 'src/utils/test-id';
import { preloadRoute } from 'src/routes/lazy-page';

import { navLinkProps } from '../nav-link-props';
import { NavItemProps } from '../types';

import { StyledNavItem } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef<HTMLDivElement, NavItemProps>(
  (
    {
      title,
      path,
      icon,
      info,
      disabled,
      caption,
      roles,
      //
      open,
      depth,
      active,
      hasChild,
      externalLink,
      currentRole = 'admin',
      onMouseEnter,
      onFocus,
      ...other
    },
    ref
  ) => {
    const subItem = depth !== 1;

    // Start loading the page on intent, so the click doesn't wait for its chunk.
    const isRouteLink = !hasChild && !disabled && !externalLink;

    const handleMouseEnter: typeof onMouseEnter = (event) => {
      if (isRouteLink) preloadRoute(path);
      onMouseEnter?.(event);
    };

    const handleFocus: typeof onFocus = (event) => {
      if (isRouteLink) preloadRoute(path);
      onFocus?.(event);
    };

    const renderContent = (
      <StyledNavItem
        disableGutters
        ref={ref}
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        data-testid={navTestId(path)}
        {...(hasChild && { 'aria-expanded': !!open })}
        {...navLinkProps({ path, active, disabled, hasChild, externalLink })}
        {...other}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
      >
        {icon && (
          <Box component="span" className="icon">
            {icon}
          </Box>
        )}

        {title && (
          <Box component="span" className="label">
            {title}
          </Box>
        )}

        {caption && (
          <Tooltip title={caption} arrow placement="right">
            <InfoSvg width={16} className="caption" />
          </Tooltip>
        )}

        {info && subItem && (
          <Box component="span" className="info">
            {info}
          </Box>
        )}

        {hasChild && <ArrowForwardSvg width={16} className="arrow" />}
      </StyledNavItem>
    );

    // Hidden item by role
    if (roles && !roles.includes(`${currentRole}`)) {
      return null;
    }

    return renderContent;
  }
);

NavItem.displayName = 'NavItem';

export { NavItem };
